/**
 * Pure core for tenant public marketing config.
 *
 * Browser-only helpers (Supabase reads, app-mode aware defaults) live in
 * `publicMarketingConfig.ts`, which re-exports everything here.
 *
 * Safe for Vercel Node: only `.js` relative imports (no extensionless browser
 * modules such as `./companyBranding`).
 */
import {
  getCodeDefaultLocalSeoCampaign,
  parseLocalSeoCampaign,
  resolveLocalSeoCampaign,
  serializeLocalSeoCampaign,
  type LocalSeoCampaignConfig,
} from './localSeoCampaign.js';

export type { LocalSeoCampaignConfig, LocalSeoCompetitorRow } from './localSeoCampaign.js';
export {
  getCodeDefaultLocalSeoCampaign,
  TB_DEFAULT_COMPETITOR_ROWS,
  TB_DEFAULT_GBP_SERVICE_AREAS,
} from './localSeoCampaign.js';

export type PublicOfficeHoursRow = {
  day: string;
  hours: string;
};

export type HubPageSeoEntry = {
  title: string;
  description: string;
};

export type RobotsAiCrawlerPolicy = 'allow' | 'disallow';

/**
 * Where schema.org aggregateRating values come from:
 * - `google`: live rating/review count from the Google Place Details proxy (manual values as fallback)
 * - `manual`: the configured manual values only
 * - `off`: omit aggregateRating from structured data entirely
 */
export type SeoRatingSource = 'google' | 'manual' | 'off';

export type ExtraKeywordLandingPage = {
  path: string;
  enabled: boolean;
  keyword: string;
  title: string;
  description: string;
  keywords: string;
  h1: string;
  intro: string;
  bullets: string[];
  faqs: Array<{ question: string; answer: string }>;
};

export type PublicMarketingConfig = {
  officeHours: PublicOfficeHoursRow[];
  contactServiceCoverage: string;
  propertyServiceCoverage: string;
  contactHeroSubtitle: string;
  /** Optional override; empty uses legacy homepage SEO corpus + substitution. */
  homepageSeoTitle: string;
  homepageSeoDescription: string;
  /** Per-path SEO overrides for marketing hub pages (e.g. `/faq`, `/contact-us`). */
  hubPageSeo: Partial<Record<string, HubPageSeoEntry>>;
  /** Per-path SEO overrides for service/location/special landing pages (e.g. `/deep-cleaning-kochi`). */
  landingPageSeo: Partial<Record<string, HubPageSeoEntry>>;
  /**
   * Extra keyword landings managed in Super Admin (not hardcoded service/location configs).
   * Each enabled path is routed, indexed in the tenant sitemap, and overridable per organisation.
   */
  extraKeywordPages: ExtraKeywordLandingPage[];
  /** Optional default og:image path or URL for homepage and hub pages (e.g. `/icon-512.png`). */
  defaultOgImageUrl: string;
  /** Additional robots.txt disallow paths merged with SaaS platform defaults. */
  robotsDisallowPaths: string[];
  /** Additional robots.txt allow paths merged with the default Allow: /. */
  robotsAllowPaths: string[];
  /** When `disallow`, block common AI training/retrieval crawlers in robots.txt. */
  robotsAiCrawlerPolicy: RobotsAiCrawlerPolicy;
  /** Custom AI crawler user-agent block list when policy is disallow (empty = SaaS defaults). */
  robotsAiCrawlerUserAgents: string[];
  /** llms.txt section ids to omit from tenant llms.txt (see LLMS_MARKETING_SECTIONS). */
  llmsDisabledSections: string[];
  /** Source policy for schema.org aggregateRating on public pages. */
  seoRatingSource: SeoRatingSource;
  /** Google Place ID used to fetch the live rating (empty = org/platform default place). */
  seoRatingGooglePlaceId: string;
  /** Manual rating value (e.g. "4.8"); used when source is manual or as live-fetch fallback. */
  seoRatingManualValue: string;
  /** Manual review count (e.g. "360"); used when source is manual or as live-fetch fallback. */
  seoRatingManualCount: string;
  /** Recent tenant SEO bundle apply events (org-scoped, max 5). */
  seoBundleHistory: TenantSeoBundleHistoryEntry[];
  /** Optional org override for SEO bundle history retention (falls back to platform default). */
  seoBundleHistoryMaxEntries?: number;
  /** Optional org override for default audit notes keyed by history source. */
  seoBundleHistoryDefaultAuditNotes?: Partial<Record<TenantSeoBundleHistoryEntry['source'], string>>;
  /** Optional org override for default audit labels keyed by history source. */
  seoBundleHistoryDefaultAuditLabels?: Partial<Record<TenantSeoBundleHistoryEntry['source'], string>>;
  /** Optional per-org override for org-admin pin permission (undefined = inherit platform). */
  seoBundleHistoryOrgPinOverride?: boolean;
  /** Optional per-org override for pin-note-required policy (undefined = inherit platform). */
  seoBundleHistoryPinNoteRequiredOverride?: boolean;
  /** Optional org override for default pin notes keyed by history source. */
  seoBundleHistoryDefaultPinNotesBySource?: Partial<Record<TenantSeoBundleHistoryEntry['source'], string>>;
  /** Hyper-local SEO campaign (HQ map, competitor matrix, GBP playbook, review request). */
  localSeo: LocalSeoCampaignConfig;
};

/**
 * Stored org/platform overlay. `localSeo` stays a sparse Super Admin patch so
 * tenants inherit platform + code defaults instead of baking T&B Kochi copy.
 */
export type PublicMarketingConfigPartial = Partial<Omit<PublicMarketingConfig, 'localSeo'>> & {
  localSeo?: Partial<LocalSeoCampaignConfig>;
};

export type TenantSeoBundleHistoryPinNoteAuditAction =
  'manual' | 'bulk_update' | 'bulk_by_source' | 'bulk_pin' | 'bulk_pin_by_source';

export type TenantSeoBundleHistoryPinNoteAuditEntry = {
  updatedAt: string;
  pinNote: string;
  action: TenantSeoBundleHistoryPinNoteAuditAction;
};

export type TenantSeoBundleHistoryEntry = {
  appliedAt: string;
  exportedAt: string;
  source: 'import' | 'platform_template' | 'org_signup' | 'rollback' | 'reapply';
  brandingCompanyName?: string;
  changeCount: number;
  /** Human-readable label for audit exports and history tables. */
  auditLabel?: string;
  /** Optional free-form note for audit context (e.g. change reason). */
  auditNote?: string;
  /** When true, entry is retained during retention prune. */
  pinned?: boolean;
  /** Optional reason why this entry is pinned (audit / retention context). */
  pinNote?: string;
  /** Recent pin note changes (newest last, capped by platform/org retention). */
  pinNoteAudit?: TenantSeoBundleHistoryPinNoteAuditEntry[];
  /** SEO field snapshot captured immediately before this apply (for rollback). */
  rollbackSnapshot?: TenantSeoBundleRollbackSnapshot;
  /** Full bundle applied in this event (for re-apply without re-import). */
  appliedBundle?: Record<string, unknown>;
};

/** SEO + robots fields captured before a bundle apply for one-click rollback. */
export type TenantSeoBundleRollbackSnapshot = {
  homepageSeoTitle: string;
  homepageSeoDescription: string;
  defaultOgImageUrl: string;
  hubPageSeo: Partial<Record<string, HubPageSeoEntry>>;
  landingPageSeo: Partial<Record<string, HubPageSeoEntry>>;
  robotsDisallowPaths: string[];
  robotsAllowPaths: string[];
  robotsAiCrawlerPolicy: RobotsAiCrawlerPolicy;
  robotsAiCrawlerUserAgents: string[];
  llmsDisabledSections: string[];
};

export const TB_DEFAULT_OFFICE_HOURS: PublicOfficeHoursRow[] = [
  { day: 'Monday – Sunday', hours: 'Open 24 hours' },
  { day: 'Public Holidays', hours: 'Open 24 hours' },
  { day: 'Emergency / Same-day', hours: 'Always available' },
];

export const TB_DEFAULT_CONTACT_SERVICE_COVERAGE =
  'Kakkanad, Edappally, Kaloor, Elamkulam, Vennala, Thevara, Vazhakkala, Palarivattom, Vyttila, Panampilly Nagar, Kadavanthra, MG Road, Marine Drive, Fort Kochi, Aluva, Thripunithura, Maradu, Nettoor, Irumpanam, and 20+ more areas across Kochi & Ernakulam. Also Kottayam, Alappuzha & Thrissur.';

export const TB_DEFAULT_PROPERTY_SERVICE_COVERAGE =
  'Kakkanad, Edappally, Kaloor, Kalamassery, Palarivattom, Vyttila, Panampilly Nagar, Kadavanthra, MG Road, Marine Drive, Fort Kochi, Aluva, Eloor, Willingdon Island, Thripunithura, Thrikkakara, Maradu, North Paravur, Kumbalangi, Angamaly, Perumbavoor and Muvattupuzha. Custom plans available across Ernakulam district.';

export const TB_DEFAULT_CONTACT_HERO_SUBTITLE =
  "Kerala's #1 professional cleaning service is a call, WhatsApp, email or short form away. Same-day service available across Kochi.";

export const SAAS_DEFAULT_OFFICE_HOURS: PublicOfficeHoursRow[] = [
  { day: 'Monday – Friday', hours: '9:00 AM – 6:00 PM' },
  { day: 'Saturday', hours: 'By appointment' },
  { day: 'Sunday / Holidays', hours: 'Emergency support only' },
];

/**
 * Code-level default config. `isSaas` selects SaaS platform vs Tools & Bubbles
 * baseline copy; pass `isSaasPlatform()` from the browser module.
 */
export function getCodeDefaultPublicMarketingConfig(isSaas = false): PublicMarketingConfig {
  if (isSaas) {
    return {
      officeHours: SAAS_DEFAULT_OFFICE_HOURS,
      contactServiceCoverage: '',
      propertyServiceCoverage: '',
      contactHeroSubtitle: '',
      homepageSeoTitle: '',
      homepageSeoDescription: '',
      hubPageSeo: {},
      landingPageSeo: {},
      extraKeywordPages: [],
      defaultOgImageUrl: '',
      robotsDisallowPaths: [],
      robotsAllowPaths: [],
      robotsAiCrawlerPolicy: 'allow',
      robotsAiCrawlerUserAgents: [],
      llmsDisabledSections: [],
      // SaaS tenants must opt in: never publish rating schema a tenant has not configured.
      seoRatingSource: 'off',
      seoRatingGooglePlaceId: '',
      seoRatingManualValue: '',
      seoRatingManualCount: '',
      seoBundleHistory: [],
      localSeo: getCodeDefaultLocalSeoCampaign(true),
    };
  }
  return {
    officeHours: TB_DEFAULT_OFFICE_HOURS,
    contactServiceCoverage: TB_DEFAULT_CONTACT_SERVICE_COVERAGE,
    propertyServiceCoverage: TB_DEFAULT_PROPERTY_SERVICE_COVERAGE,
    contactHeroSubtitle: TB_DEFAULT_CONTACT_HERO_SUBTITLE,
    homepageSeoTitle: '',
    homepageSeoDescription: '',
    hubPageSeo: {},
    landingPageSeo: {},
    extraKeywordPages: [],
    defaultOgImageUrl: '',
    robotsDisallowPaths: [],
    robotsAllowPaths: [],
    robotsAiCrawlerPolicy: 'allow',
    robotsAiCrawlerUserAgents: [],
    llmsDisabledSections: [],
    // T&B baseline: live Google rating; fallback matches the verified profile (never inflate).
    seoRatingSource: 'google',
    seoRatingGooglePlaceId: '',
    seoRatingManualValue: '4.8',
    seoRatingManualCount: '237',
    seoBundleHistory: [],
    localSeo: getCodeDefaultLocalSeoCampaign(false),
  };
}

function parseOfficeHours(raw: unknown): PublicOfficeHoursRow[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const rows = raw
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const item = row as Record<string, unknown>;
      const day = String(item.day ?? '').trim();
      const hours = String(item.hours ?? '').trim();
      if (!day || !hours) return null;
      return { day, hours };
    })
    .filter((row): row is PublicOfficeHoursRow => row !== null);
  return rows.length > 0 ? rows : null;
}

function parsePathSeoMap(raw: unknown): Partial<Record<string, HubPageSeoEntry>> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const result: Partial<Record<string, HubPageSeoEntry>> = {};
  for (const [path, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!value || typeof value !== 'object') continue;
    const entry = value as Record<string, unknown>;
    const title = String(entry.title ?? '').trim();
    const description = String(entry.description ?? '').trim();
    if (title || description) {
      result[path] = { title, description };
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

function normalizeKeywordLandingPath(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return '';
  const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return withSlash.replace(/\/+$/, '') || '/';
}

function parseExtraKeywordPages(raw: unknown): ExtraKeywordLandingPage[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const pages: ExtraKeywordLandingPage[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const item = row as Record<string, unknown>;
    const path = normalizeKeywordLandingPath(String(item.path ?? ''));
    if (!path || path === '/') continue;
    const bullets = Array.isArray(item.bullets)
      ? item.bullets.map((b) => String(b ?? '').trim()).filter(Boolean)
      : String(item.bullets ?? '')
          .split('\n')
          .map((b) => b.trim())
          .filter(Boolean);
    const faqs = Array.isArray(item.faqs)
      ? item.faqs
          .map((faq) => {
            if (!faq || typeof faq !== 'object') return null;
            const entry = faq as Record<string, unknown>;
            const question = String(entry.question ?? entry.q ?? '').trim();
            const answer = String(entry.answer ?? entry.a ?? '').trim();
            if (!question || !answer) return null;
            return { question, answer };
          })
          .filter((faq): faq is { question: string; answer: string } => faq !== null)
      : [];
    const enabledRaw = item.enabled;
    pages.push({
      path,
      enabled: enabledRaw === false || enabledRaw === 'false' || enabledRaw === 0 ? false : true,
      keyword: String(item.keyword ?? '').trim(),
      title: String(item.title ?? '').trim(),
      description: String(item.description ?? '').trim(),
      keywords: String(item.keywords ?? '').trim(),
      h1: String(item.h1 ?? '').trim(),
      intro: String(item.intro ?? '').trim(),
      bullets,
      faqs,
    });
  }
  return pages.length > 0 ? pages : undefined;
}

function mergeExtraKeywordPages(
  defaults: ExtraKeywordLandingPage[],
  platform: ExtraKeywordLandingPage[] | undefined,
  org: ExtraKeywordLandingPage[] | undefined,
): ExtraKeywordLandingPage[] {
  const byPath = new Map<string, ExtraKeywordLandingPage>();
  for (const page of [...defaults, ...(platform ?? []), ...(org ?? [])]) {
    const path = normalizeKeywordLandingPath(page.path);
    if (!path || path === '/') continue;
    byPath.set(path, { ...page, path });
  }
  return [...byPath.values()];
}

function parseHubPageSeo(raw: unknown): Partial<Record<string, HubPageSeoEntry>> | undefined {
  return parsePathSeoMap(raw);
}

function mergePathSeoMap(
  ...layers: Array<Partial<Record<string, HubPageSeoEntry>> | undefined>
): Partial<Record<string, HubPageSeoEntry>> {
  const merged: Partial<Record<string, HubPageSeoEntry>> = {};
  for (const layer of layers) {
    if (!layer) continue;
    for (const [path, entry] of Object.entries(layer)) {
      if (!entry) continue;
      merged[path] = {
        title: entry.title?.trim() || merged[path]?.title || '',
        description: entry.description?.trim() || merged[path]?.description || '',
      };
    }
  }
  for (const path of Object.keys(merged)) {
    const entry = merged[path];
    if (!entry?.title?.trim() && !entry?.description?.trim()) {
      delete merged[path];
    }
  }
  return merged;
}

function mergeRobotsPathLayers(...layers: Array<string[] | undefined>): string[] {
  const merged = new Set<string>();
  for (const layer of layers) {
    for (const path of layer ?? []) {
      const trimmed = path.trim();
      if (!trimmed) continue;
      merged.add(trimmed.startsWith('/') ? trimmed : `/${trimmed}`);
    }
  }
  return [...merged];
}

function parseRobotsDisallowPaths(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const paths = raw.map((item) => String(item).trim()).filter(Boolean);
  return paths.length > 0 ? paths : undefined;
}

function parseRobotsAllowPaths(raw: unknown): string[] | undefined {
  return parseRobotsDisallowPaths(raw);
}

function parseRobotsAiCrawlerPolicy(raw: unknown): RobotsAiCrawlerPolicy | undefined {
  const value = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (value === 'disallow') return 'disallow';
  if (value === 'allow') return 'allow';
  return undefined;
}

function parseRobotsAiCrawlerUserAgents(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const agents = raw.map((item) => String(item).trim()).filter(Boolean);
  return agents.length > 0 ? agents : undefined;
}

function parseLlmsDisabledSections(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const sections = raw.map((item) => String(item).trim()).filter(Boolean);
  return sections.length > 0 ? sections : undefined;
}

function parseSeoRatingSource(raw: unknown): SeoRatingSource | undefined {
  const value = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (value === 'google' || value === 'manual' || value === 'off') return value;
  return undefined;
}

/** Rating value must be a number in (0, 5]; returns normalized one-decimal string. */
function parseSeoRatingManualValue(raw: unknown): string | undefined {
  const value = Number(String(raw ?? '').trim());
  if (!Number.isFinite(value) || value <= 0 || value > 5) return undefined;
  return value.toFixed(1);
}

/** Review count must be a positive integer; returns normalized string. */
function parseSeoRatingManualCount(raw: unknown): string | undefined {
  const value = Number(String(raw ?? '').trim());
  if (!Number.isFinite(value) || value < 1) return undefined;
  return String(Math.round(value));
}

const SEO_BUNDLE_HISTORY_SOURCES = new Set<TenantSeoBundleHistoryEntry['source']>([
  'import',
  'platform_template',
  'org_signup',
  'rollback',
  'reapply',
]);

function parseTenantSeoBundleRollbackSnapshot(raw: unknown): TenantSeoBundleRollbackSnapshot | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const row = raw as Record<string, unknown>;
  return {
    homepageSeoTitle: String(row.homepage_seo_title ?? row.homepageSeoTitle ?? '').trim(),
    homepageSeoDescription: String(row.homepage_seo_description ?? row.homepageSeoDescription ?? '').trim(),
    defaultOgImageUrl: String(row.default_og_image_url ?? row.defaultOgImageUrl ?? '').trim(),
    hubPageSeo: parsePathSeoMap(row.hub_page_seo ?? row.hubPageSeo) ?? {},
    landingPageSeo: parsePathSeoMap(row.landing_page_seo ?? row.landingPageSeo) ?? {},
    robotsDisallowPaths: parseRobotsDisallowPaths(row.robots_disallow_paths) ?? [],
    robotsAllowPaths: parseRobotsAllowPaths(row.robots_allow_paths) ?? [],
    robotsAiCrawlerPolicy: parseRobotsAiCrawlerPolicy(row.robots_ai_crawler_policy) ?? 'allow',
    robotsAiCrawlerUserAgents: parseRobotsAiCrawlerUserAgents(row.robots_ai_crawler_user_agents) ?? [],
    llmsDisabledSections: parseLlmsDisabledSections(row.llms_disabled_sections) ?? [],
  };
}

function serializeTenantSeoBundleRollbackSnapshot(
  snapshot: TenantSeoBundleRollbackSnapshot,
): Record<string, unknown> {
  return {
    homepage_seo_title: snapshot.homepageSeoTitle,
    homepage_seo_description: snapshot.homepageSeoDescription,
    default_og_image_url: snapshot.defaultOgImageUrl,
    hub_page_seo: snapshot.hubPageSeo,
    landing_page_seo: snapshot.landingPageSeo,
    robots_disallow_paths: snapshot.robotsDisallowPaths,
    robots_allow_paths: snapshot.robotsAllowPaths,
    robots_ai_crawler_policy: snapshot.robotsAiCrawlerPolicy,
    robots_ai_crawler_user_agents: snapshot.robotsAiCrawlerUserAgents,
    llms_disabled_sections: snapshot.llmsDisabledSections,
  };
}

function serializeSeoBundleHistoryDefaultAuditLabels(
  labels: PublicMarketingConfig['seoBundleHistoryDefaultAuditLabels'],
): Record<string, string> | undefined {
  if (!labels) return undefined;
  const serialized: Record<string, string> = {};
  for (const source of SEO_BUNDLE_HISTORY_SOURCES) {
    const value = labels[source]?.trim();
    if (value) serialized[source] = value.slice(0, 200);
  }
  return Object.keys(serialized).length > 0 ? serialized : undefined;
}

function parseSeoBundleHistoryDefaultAuditLabels(
  raw: unknown,
): PublicMarketingConfig['seoBundleHistoryDefaultAuditLabels'] | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const row = raw as Record<string, unknown>;
  const labels: NonNullable<PublicMarketingConfig['seoBundleHistoryDefaultAuditLabels']> = {};
  for (const source of SEO_BUNDLE_HISTORY_SOURCES) {
    const value = String(row[source] ?? '').trim();
    if (value) labels[source] = value.slice(0, 200);
  }
  return Object.keys(labels).length > 0 ? labels : undefined;
}

function parseSeoBundleHistoryDefaultAuditNotes(
  raw: unknown,
): PublicMarketingConfig['seoBundleHistoryDefaultAuditNotes'] | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const row = raw as Record<string, unknown>;
  const notes: NonNullable<PublicMarketingConfig['seoBundleHistoryDefaultAuditNotes']> = {};
  for (const source of SEO_BUNDLE_HISTORY_SOURCES) {
    const value = String(row[source] ?? '').trim();
    if (value) notes[source] = value.slice(0, 500);
  }
  return Object.keys(notes).length > 0 ? notes : undefined;
}

function parseSeoBundleHistoryDefaultPinNotesBySource(
  raw: unknown,
): PublicMarketingConfig['seoBundleHistoryDefaultPinNotesBySource'] | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const row = raw as Record<string, unknown>;
  const notes: NonNullable<PublicMarketingConfig['seoBundleHistoryDefaultPinNotesBySource']> = {};
  for (const source of SEO_BUNDLE_HISTORY_SOURCES) {
    const value = String(row[source] ?? '').trim();
    if (value) notes[source] = value.slice(0, 500);
  }
  return Object.keys(notes).length > 0 ? notes : undefined;
}

function serializeSeoBundleHistoryDefaultPinNotesBySource(
  notes: PublicMarketingConfig['seoBundleHistoryDefaultPinNotesBySource'],
): Record<string, string> | undefined {
  if (!notes) return undefined;
  const serialized: Record<string, string> = {};
  for (const source of SEO_BUNDLE_HISTORY_SOURCES) {
    const value = notes[source]?.trim();
    if (value) serialized[source] = value.slice(0, 500);
  }
  return Object.keys(serialized).length > 0 ? serialized : undefined;
}

function serializeSeoBundleHistoryDefaultAuditNotes(
  notes: PublicMarketingConfig['seoBundleHistoryDefaultAuditNotes'],
): Record<string, string> | undefined {
  if (!notes) return undefined;
  const serialized: Record<string, string> = {};
  for (const source of SEO_BUNDLE_HISTORY_SOURCES) {
    const value = notes[source]?.trim();
    if (value) serialized[source] = value.slice(0, 500);
  }
  return Object.keys(serialized).length > 0 ? serialized : undefined;
}

function parseSeoBundleHistory(raw: unknown): TenantSeoBundleHistoryEntry[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const entries = raw
    .map((item): TenantSeoBundleHistoryEntry | null => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const source = String(row.source ?? '').trim() as TenantSeoBundleHistoryEntry['source'];
      const appliedAt = String(row.applied_at ?? row.appliedAt ?? '').trim();
      const exportedAt = String(row.exported_at ?? row.exportedAt ?? '').trim();
      if (!SEO_BUNDLE_HISTORY_SOURCES.has(source) || !appliedAt || !exportedAt) return null;
      const changeCount = Number(row.change_count ?? row.changeCount ?? 0);
      const brandingCompanyName = String(row.branding_company_name ?? row.brandingCompanyName ?? '').trim();
      const rollbackSnapshot = parseTenantSeoBundleRollbackSnapshot(
        row.rollback_snapshot ?? row.rollbackSnapshot,
      );
      const appliedBundleRaw = row.applied_bundle ?? row.appliedBundle;
      const appliedBundle =
        appliedBundleRaw && typeof appliedBundleRaw === 'object' && appliedBundleRaw !== null
          ? (appliedBundleRaw as Record<string, unknown>)
          : undefined;
      const auditLabel = String(row.audit_label ?? row.auditLabel ?? '').trim();
      const auditNote = String(row.audit_note ?? row.auditNote ?? '').trim();
      const pinnedRaw = row.pinned ?? row.is_pinned ?? row.isPinned;
      const pinned = pinnedRaw === true || pinnedRaw === 'true' || pinnedRaw === 1 || pinnedRaw === '1';
      const pinNote = String(row.pin_note ?? row.pinNote ?? '').trim();
      const pinNoteAuditRaw = row.pin_note_audit ?? row.pinNoteAudit;
      const pinNoteAudit = Array.isArray(pinNoteAuditRaw)
        ? pinNoteAuditRaw
            .map((item) => {
              if (!item || typeof item !== 'object') return null;
              const auditRow = item as Record<string, unknown>;
              const updatedAt = String(auditRow.updated_at ?? auditRow.updatedAt ?? '').trim();
              const auditPinNote = String(auditRow.pin_note ?? auditRow.pinNote ?? '').trim();
              const action = String(auditRow.action ?? '').trim() as TenantSeoBundleHistoryPinNoteAuditAction;
              if (!updatedAt || !auditPinNote) return null;
              if (
                action !== 'manual' &&
                action !== 'bulk_update' &&
                action !== 'bulk_by_source' &&
                action !== 'bulk_pin' &&
                action !== 'bulk_pin_by_source'
              ) {
                return null;
              }
              return {
                updatedAt,
                pinNote: auditPinNote.slice(0, 500),
                action,
              } satisfies TenantSeoBundleHistoryPinNoteAuditEntry;
            })
            .filter((item): item is TenantSeoBundleHistoryPinNoteAuditEntry => item !== null)
            .slice(-5)
        : undefined;
      return {
        appliedAt,
        exportedAt,
        source,
        ...(brandingCompanyName ? { brandingCompanyName } : {}),
        changeCount: Number.isFinite(changeCount) ? Math.max(0, changeCount) : 0,
        ...(auditLabel ? { auditLabel } : {}),
        ...(auditNote ? { auditNote } : {}),
        ...(pinned ? { pinned: true } : {}),
        ...(pinNote ? { pinNote: pinNote.slice(0, 500) } : {}),
        ...(pinNoteAudit && pinNoteAudit.length > 0 ? { pinNoteAudit } : {}),
        ...(rollbackSnapshot ? { rollbackSnapshot } : {}),
        ...(appliedBundle ? { appliedBundle } : {}),
      } satisfies TenantSeoBundleHistoryEntry;
    })
    .filter((entry): entry is TenantSeoBundleHistoryEntry => entry !== null);
  return entries.length > 0 ? entries.slice(0, TENANT_SEO_BUNDLE_HISTORY_HARD_CAP) : undefined;
}

export function parsePublicMarketingConfig(raw: unknown): PublicMarketingConfigPartial {
  if (!raw || typeof raw !== 'object') return {};
  const o = raw as Record<string, unknown>;
  const officeHours = parseOfficeHours(o.office_hours);
  const contactServiceCoverage = String(o.contact_service_coverage ?? '').trim();
  const propertyServiceCoverage = String(o.property_service_coverage ?? '').trim();
  const contactHeroSubtitle = String(o.contact_hero_subtitle ?? '').trim();
  const homepageSeoTitle = String(o.homepage_seo_title ?? '').trim();
  const homepageSeoDescription = String(o.homepage_seo_description ?? '').trim();
  const hubPageSeo = parseHubPageSeo(o.hub_page_seo);
  const landingPageSeo = parsePathSeoMap(o.landing_page_seo);
  const extraKeywordPages = parseExtraKeywordPages(o.extra_keyword_pages ?? o.extraKeywordPages);
  const defaultOgImageUrl = String(o.default_og_image_url ?? '').trim();
  const robotsDisallowPaths = parseRobotsDisallowPaths(o.robots_disallow_paths);
  const robotsAllowPaths = parseRobotsAllowPaths(o.robots_allow_paths);
  const robotsAiCrawlerPolicy = parseRobotsAiCrawlerPolicy(o.robots_ai_crawler_policy);
  const robotsAiCrawlerUserAgents = parseRobotsAiCrawlerUserAgents(o.robots_ai_crawler_user_agents);
  const llmsDisabledSections = parseLlmsDisabledSections(o.llms_disabled_sections);
  const seoRatingSource = parseSeoRatingSource(o.seo_rating_source);
  const seoRatingGooglePlaceId = String(o.seo_rating_google_place_id ?? '').trim();
  const seoRatingManualValue = parseSeoRatingManualValue(o.seo_rating_manual_value);
  const seoRatingManualCount = parseSeoRatingManualCount(o.seo_rating_manual_count);
  const seoBundleHistory = parseSeoBundleHistory(o.seo_bundle_history);
  const seoBundleHistoryMaxEntriesRaw = Number(
    o.seo_bundle_history_max_entries ?? o.seoBundleHistoryMaxEntries,
  );
  const seoBundleHistoryMaxEntries = Number.isFinite(seoBundleHistoryMaxEntriesRaw)
    ? seoBundleHistoryMaxEntriesRaw
    : undefined;
  const seoBundleHistoryDefaultAuditNotes = parseSeoBundleHistoryDefaultAuditNotes(
    o.seo_bundle_history_default_audit_notes ?? o.seoBundleHistoryDefaultAuditNotes,
  );
  const seoBundleHistoryDefaultAuditLabels = parseSeoBundleHistoryDefaultAuditLabels(
    o.seo_bundle_history_default_audit_labels ?? o.seoBundleHistoryDefaultAuditLabels,
  );
  const seoBundleHistoryOrgPinOverrideRaw =
    o.seo_bundle_history_org_pin_override ?? o.seoBundleHistoryOrgPinOverride;
  const seoBundleHistoryOrgPinOverride =
    seoBundleHistoryOrgPinOverrideRaw === true ||
    seoBundleHistoryOrgPinOverrideRaw === 'true' ||
    seoBundleHistoryOrgPinOverrideRaw === 1 ||
    seoBundleHistoryOrgPinOverrideRaw === '1'
      ? true
      : seoBundleHistoryOrgPinOverrideRaw === false ||
          seoBundleHistoryOrgPinOverrideRaw === 'false' ||
          seoBundleHistoryOrgPinOverrideRaw === 0 ||
          seoBundleHistoryOrgPinOverrideRaw === '0'
        ? false
        : undefined;
  const seoBundleHistoryPinNoteRequiredOverrideRaw =
    o.seo_bundle_history_pin_note_required_override ?? o.seoBundleHistoryPinNoteRequiredOverride;
  const seoBundleHistoryPinNoteRequiredOverride =
    seoBundleHistoryPinNoteRequiredOverrideRaw === true ||
    seoBundleHistoryPinNoteRequiredOverrideRaw === 'true' ||
    seoBundleHistoryPinNoteRequiredOverrideRaw === 1 ||
    seoBundleHistoryPinNoteRequiredOverrideRaw === '1'
      ? true
      : seoBundleHistoryPinNoteRequiredOverrideRaw === false ||
          seoBundleHistoryPinNoteRequiredOverrideRaw === 'false' ||
          seoBundleHistoryPinNoteRequiredOverrideRaw === 0 ||
          seoBundleHistoryPinNoteRequiredOverrideRaw === '0'
        ? false
        : undefined;
  const seoBundleHistoryDefaultPinNotesBySource = parseSeoBundleHistoryDefaultPinNotesBySource(
    o.seo_bundle_history_default_pin_notes_by_source ?? o.seoBundleHistoryDefaultPinNotesBySource,
  );
  const localSeo = parseLocalSeoCampaign(o.local_seo ?? o.localSeo);
  return {
    ...(officeHours ? { officeHours } : {}),
    ...(contactServiceCoverage ? { contactServiceCoverage } : {}),
    ...(propertyServiceCoverage ? { propertyServiceCoverage } : {}),
    ...(contactHeroSubtitle ? { contactHeroSubtitle } : {}),
    ...(homepageSeoTitle ? { homepageSeoTitle } : {}),
    ...(homepageSeoDescription ? { homepageSeoDescription } : {}),
    ...(hubPageSeo ? { hubPageSeo } : {}),
    ...(landingPageSeo ? { landingPageSeo } : {}),
    ...(extraKeywordPages ? { extraKeywordPages } : {}),
    ...(defaultOgImageUrl ? { defaultOgImageUrl } : {}),
    ...(robotsDisallowPaths ? { robotsDisallowPaths } : {}),
    ...(robotsAllowPaths ? { robotsAllowPaths } : {}),
    ...(robotsAiCrawlerPolicy ? { robotsAiCrawlerPolicy } : {}),
    ...(robotsAiCrawlerUserAgents ? { robotsAiCrawlerUserAgents } : {}),
    ...(llmsDisabledSections ? { llmsDisabledSections } : {}),
    ...(seoRatingSource ? { seoRatingSource } : {}),
    ...(seoRatingGooglePlaceId ? { seoRatingGooglePlaceId } : {}),
    ...(seoRatingManualValue ? { seoRatingManualValue } : {}),
    ...(seoRatingManualCount ? { seoRatingManualCount } : {}),
    ...(seoBundleHistory ? { seoBundleHistory } : {}),
    ...(seoBundleHistoryMaxEntries !== undefined ? { seoBundleHistoryMaxEntries } : {}),
    ...(seoBundleHistoryDefaultAuditNotes ? { seoBundleHistoryDefaultAuditNotes } : {}),
    ...(seoBundleHistoryDefaultAuditLabels ? { seoBundleHistoryDefaultAuditLabels } : {}),
    ...(seoBundleHistoryOrgPinOverride !== undefined ? { seoBundleHistoryOrgPinOverride } : {}),
    ...(seoBundleHistoryPinNoteRequiredOverride !== undefined
      ? { seoBundleHistoryPinNoteRequiredOverride }
      : {}),
    ...(seoBundleHistoryDefaultPinNotesBySource ? { seoBundleHistoryDefaultPinNotesBySource } : {}),
    ...(localSeo ? { localSeo } : {}),
  };
}

export function serializePublicMarketingConfig(config: PublicMarketingConfig): Record<string, unknown> {
  return {
    office_hours: config.officeHours,
    contact_service_coverage: config.contactServiceCoverage,
    property_service_coverage: config.propertyServiceCoverage,
    contact_hero_subtitle: config.contactHeroSubtitle,
    homepage_seo_title: config.homepageSeoTitle,
    homepage_seo_description: config.homepageSeoDescription,
    hub_page_seo: Object.fromEntries(
      Object.entries(config.hubPageSeo).filter(([, entry]) =>
        Boolean(entry?.title.trim() || entry?.description.trim()),
      ),
    ),
    landing_page_seo: Object.fromEntries(
      Object.entries(config.landingPageSeo).filter(
        ([path, entry]) => path.length > 1 && Boolean(entry?.title.trim() || entry?.description.trim()),
      ),
    ),
    ...(config.extraKeywordPages?.length
      ? {
          extra_keyword_pages: config.extraKeywordPages.map((page) => ({
            path: page.path,
            enabled: page.enabled,
            keyword: page.keyword,
            title: page.title,
            description: page.description,
            keywords: page.keywords,
            h1: page.h1,
            intro: page.intro,
            bullets: page.bullets,
            faqs: page.faqs,
          })),
        }
      : {}),
    ...(config.defaultOgImageUrl?.trim() ? { default_og_image_url: config.defaultOgImageUrl.trim() } : {}),
    ...(config.robotsDisallowPaths?.length ? { robots_disallow_paths: config.robotsDisallowPaths } : {}),
    ...(config.robotsAllowPaths?.length ? { robots_allow_paths: config.robotsAllowPaths } : {}),
    ...(config.robotsAiCrawlerPolicy && config.robotsAiCrawlerPolicy !== 'allow'
      ? { robots_ai_crawler_policy: config.robotsAiCrawlerPolicy }
      : {}),
    ...(config.robotsAiCrawlerUserAgents?.length
      ? { robots_ai_crawler_user_agents: config.robotsAiCrawlerUserAgents }
      : {}),
    ...(config.llmsDisabledSections?.length ? { llms_disabled_sections: config.llmsDisabledSections } : {}),
    ...(config.seoRatingSource ? { seo_rating_source: config.seoRatingSource } : {}),
    ...(config.seoRatingGooglePlaceId?.trim()
      ? { seo_rating_google_place_id: config.seoRatingGooglePlaceId.trim() }
      : {}),
    ...(config.seoRatingManualValue?.trim()
      ? { seo_rating_manual_value: config.seoRatingManualValue.trim() }
      : {}),
    ...(config.seoRatingManualCount?.trim()
      ? { seo_rating_manual_count: config.seoRatingManualCount.trim() }
      : {}),
    ...(config.seoBundleHistory?.length
      ? {
          seo_bundle_history: config.seoBundleHistory
            .slice(0, TENANT_SEO_BUNDLE_HISTORY_HARD_CAP)
            .map((entry) => ({
              applied_at: entry.appliedAt,
              exported_at: entry.exportedAt,
              source: entry.source,
              ...(entry.brandingCompanyName ? { branding_company_name: entry.brandingCompanyName } : {}),
              change_count: entry.changeCount,
              ...(entry.auditLabel ? { audit_label: entry.auditLabel } : {}),
              ...(entry.auditNote ? { audit_note: entry.auditNote } : {}),
              ...(entry.pinned ? { pinned: true } : {}),
              ...(entry.pinNote ? { pin_note: entry.pinNote } : {}),
              ...(entry.rollbackSnapshot
                ? { rollback_snapshot: serializeTenantSeoBundleRollbackSnapshot(entry.rollbackSnapshot) }
                : {}),
              ...(entry.appliedBundle ? { applied_bundle: entry.appliedBundle } : {}),
            })),
        }
      : {}),
    ...(config.seoBundleHistoryMaxEntries !== undefined
      ? { seo_bundle_history_max_entries: config.seoBundleHistoryMaxEntries }
      : {}),
    ...(serializeSeoBundleHistoryDefaultAuditNotes(config.seoBundleHistoryDefaultAuditNotes)
      ? {
          seo_bundle_history_default_audit_notes: serializeSeoBundleHistoryDefaultAuditNotes(
            config.seoBundleHistoryDefaultAuditNotes,
          ),
        }
      : {}),
    ...(serializeSeoBundleHistoryDefaultAuditLabels(config.seoBundleHistoryDefaultAuditLabels)
      ? {
          seo_bundle_history_default_audit_labels: serializeSeoBundleHistoryDefaultAuditLabels(
            config.seoBundleHistoryDefaultAuditLabels,
          ),
        }
      : {}),
    ...(config.seoBundleHistoryOrgPinOverride !== undefined
      ? { seo_bundle_history_org_pin_override: config.seoBundleHistoryOrgPinOverride }
      : {}),
    ...(config.seoBundleHistoryPinNoteRequiredOverride !== undefined
      ? {
          seo_bundle_history_pin_note_required_override: config.seoBundleHistoryPinNoteRequiredOverride,
        }
      : {}),
    ...(serializeSeoBundleHistoryDefaultPinNotesBySource(config.seoBundleHistoryDefaultPinNotesBySource)
      ? {
          seo_bundle_history_default_pin_notes_by_source: serializeSeoBundleHistoryDefaultPinNotesBySource(
            config.seoBundleHistoryDefaultPinNotesBySource,
          ),
        }
      : {}),
    ...(config.localSeo ? { local_seo: serializeLocalSeoCampaign(config.localSeo) } : {}),
  };
}

/**
 * Merge org + platform partials over code defaults. `isSaas` selects the code
 * default baseline copy (pass `isSaasPlatform()` from the browser module).
 */
export function resolvePublicMarketingConfig(
  orgPartial?: PublicMarketingConfigPartial | null,
  platformPartial?: PublicMarketingConfigPartial | null,
  isSaas = false,
): PublicMarketingConfig {
  const defaults = getCodeDefaultPublicMarketingConfig(isSaas);
  const platform = platformPartial ?? {};
  const org = orgPartial ?? {};
  return {
    officeHours: org.officeHours?.length
      ? org.officeHours
      : platform.officeHours?.length
        ? platform.officeHours
        : defaults.officeHours,
    contactServiceCoverage:
      org.contactServiceCoverage || platform.contactServiceCoverage || defaults.contactServiceCoverage,
    propertyServiceCoverage:
      org.propertyServiceCoverage || platform.propertyServiceCoverage || defaults.propertyServiceCoverage,
    contactHeroSubtitle:
      org.contactHeroSubtitle || platform.contactHeroSubtitle || defaults.contactHeroSubtitle,
    homepageSeoTitle: org.homepageSeoTitle || platform.homepageSeoTitle || defaults.homepageSeoTitle,
    homepageSeoDescription:
      org.homepageSeoDescription || platform.homepageSeoDescription || defaults.homepageSeoDescription,
    hubPageSeo: mergePathSeoMap(defaults.hubPageSeo, platform.hubPageSeo, org.hubPageSeo),
    landingPageSeo: mergePathSeoMap(defaults.landingPageSeo, platform.landingPageSeo, org.landingPageSeo),
    extraKeywordPages: mergeExtraKeywordPages(
      defaults.extraKeywordPages,
      platform.extraKeywordPages,
      org.extraKeywordPages,
    ),
    defaultOgImageUrl: org.defaultOgImageUrl || platform.defaultOgImageUrl || defaults.defaultOgImageUrl,
    robotsDisallowPaths: mergeRobotsPathLayers(platform.robotsDisallowPaths, org.robotsDisallowPaths),
    robotsAllowPaths: mergeRobotsPathLayers(platform.robotsAllowPaths, org.robotsAllowPaths),
    robotsAiCrawlerPolicy:
      org.robotsAiCrawlerPolicy || platform.robotsAiCrawlerPolicy || defaults.robotsAiCrawlerPolicy,
    robotsAiCrawlerUserAgents: org.robotsAiCrawlerUserAgents?.length
      ? org.robotsAiCrawlerUserAgents
      : platform.robotsAiCrawlerUserAgents?.length
        ? platform.robotsAiCrawlerUserAgents
        : defaults.robotsAiCrawlerUserAgents,
    llmsDisabledSections: org.llmsDisabledSections?.length
      ? org.llmsDisabledSections
      : platform.llmsDisabledSections?.length
        ? platform.llmsDisabledSections
        : defaults.llmsDisabledSections,
    seoRatingSource: org.seoRatingSource || platform.seoRatingSource || defaults.seoRatingSource,
    seoRatingGooglePlaceId:
      org.seoRatingGooglePlaceId || platform.seoRatingGooglePlaceId || defaults.seoRatingGooglePlaceId,
    seoRatingManualValue:
      org.seoRatingManualValue || platform.seoRatingManualValue || defaults.seoRatingManualValue,
    seoRatingManualCount:
      org.seoRatingManualCount || platform.seoRatingManualCount || defaults.seoRatingManualCount,
    seoBundleHistory: org.seoBundleHistory?.length
      ? org.seoBundleHistory.slice(0, TENANT_SEO_BUNDLE_HISTORY_HARD_CAP)
      : defaults.seoBundleHistory,
    seoBundleHistoryMaxEntries:
      org.seoBundleHistoryMaxEntries !== undefined
        ? org.seoBundleHistoryMaxEntries
        : defaults.seoBundleHistoryMaxEntries,
    seoBundleHistoryDefaultAuditNotes:
      org.seoBundleHistoryDefaultAuditNotes && Object.keys(org.seoBundleHistoryDefaultAuditNotes).length > 0
        ? org.seoBundleHistoryDefaultAuditNotes
        : defaults.seoBundleHistoryDefaultAuditNotes,
    seoBundleHistoryDefaultAuditLabels:
      org.seoBundleHistoryDefaultAuditLabels && Object.keys(org.seoBundleHistoryDefaultAuditLabels).length > 0
        ? org.seoBundleHistoryDefaultAuditLabels
        : defaults.seoBundleHistoryDefaultAuditLabels,
    seoBundleHistoryOrgPinOverride:
      org.seoBundleHistoryOrgPinOverride !== undefined
        ? org.seoBundleHistoryOrgPinOverride
        : defaults.seoBundleHistoryOrgPinOverride,
    seoBundleHistoryPinNoteRequiredOverride:
      org.seoBundleHistoryPinNoteRequiredOverride !== undefined
        ? org.seoBundleHistoryPinNoteRequiredOverride
        : defaults.seoBundleHistoryPinNoteRequiredOverride,
    seoBundleHistoryDefaultPinNotesBySource:
      org.seoBundleHistoryDefaultPinNotesBySource &&
      Object.keys(org.seoBundleHistoryDefaultPinNotesBySource).length > 0
        ? org.seoBundleHistoryDefaultPinNotesBySource
        : defaults.seoBundleHistoryDefaultPinNotesBySource,
    localSeo: resolveLocalSeoCampaign(org.localSeo, platform.localSeo, isSaas),
  };
}

export const PUBLIC_MARKETING_PLATFORM_CONFIG_KEY = 'saas_default_public_marketing_config';

/** Platform super-admin: default tenant SEO bundle template (robots/sitemap/llms + overrides). */
export const PUBLIC_MARKETING_PLATFORM_SEO_BUNDLE_TEMPLATE_KEY = 'saas_default_tenant_seo_bundle_template';

/** When true, apply platform SEO bundle template to new orgs on signup. */
export const PUBLIC_MARKETING_AUTO_APPLY_SEO_BUNDLE_TEMPLATE_KEY =
  'saas_auto_apply_tenant_seo_bundle_template';

/** Platform super-admin: max SEO bundle history entries retained per tenant org. */
export const PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_MAX_KEY =
  'saas_tenant_seo_bundle_history_max_entries';

/** Platform super-admin: max number of named saved history filter views per tenant scope. */
export const PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_SAVED_VIEWS_LIMIT_KEY =
  'saas_tenant_seo_bundle_history_saved_views_limit';

/** Platform super-admin: weekly pg_cron prune of orphan tenant SEO bundle history. */
export const PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_PRUNE_CRON_KEY =
  'saas_tenant_seo_bundle_history_prune_cron_enabled';

/** Platform super-admin: quick-apply audit label presets for tenant SEO bundle history. */
export const PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_PRESETS_KEY =
  'saas_tenant_seo_bundle_history_audit_label_presets';

/** Platform super-admin: placeholder hint for tenant SEO bundle history audit notes. */
export const PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_PLACEHOLDER_KEY =
  'saas_tenant_seo_bundle_history_audit_note_placeholder';

/** Platform super-admin: default audit notes keyed by history source. */
export const PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_BY_SOURCE_KEY =
  'saas_tenant_seo_bundle_history_audit_note_by_source';

/** Platform super-admin: default audit labels keyed by history source. */
export const PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_BY_SOURCE_KEY =
  'saas_tenant_seo_bundle_history_audit_label_by_source';

/** Platform super-admin: org vs platform history entry pin permissions. */
export const PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_PIN_POLICY_KEY =
  'saas_tenant_seo_bundle_history_pin_policy';

/** Platform super-admin: default note applied when bulk-pinning history entries. */
export const PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_DEFAULT_PIN_NOTE_KEY =
  'saas_tenant_seo_bundle_history_default_pin_note';

/** Platform super-admin: default pin notes keyed by history source. */
export const PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_PIN_NOTE_BY_SOURCE_KEY =
  'saas_tenant_seo_bundle_history_pin_note_by_source';

/** Platform super-admin: pinned + source export presets for history audit. */
export const PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESETS_KEY =
  'saas_tenant_seo_bundle_history_export_presets';

/** Platform super-admin: max export presets loaded/saved from app_config (not org draft textarea). */
export const PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PLATFORM_PRESET_LIMIT_KEY =
  'saas_tenant_seo_bundle_history_export_platform_preset_limit';

/** Platform super-admin: preset count at which label search UI appears in history panels. */
export const PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_LABEL_SEARCH_MIN_KEY =
  'saas_tenant_seo_bundle_history_export_preset_label_search_min';

/** Platform super-admin: dimension for grouping export preset chips in history panels. */
export const PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_GROUP_BY_KEY =
  'saas_tenant_seo_bundle_history_export_preset_group_by';

/** Platform super-admin: max export preset chips shown before expand toggle. */
export const PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_EXPAND_LIMIT_KEY =
  'saas_tenant_seo_bundle_history_export_preset_chip_expand_limit';

/** Platform super-admin: page size for paginated export preset chips when expanded. */
export const PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_SIZE_KEY =
  'saas_tenant_seo_bundle_history_export_preset_chip_page_size';

/** Platform super-admin: persist expanded export preset chip page in session storage. */
export const PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_PERSIST_ENABLED_KEY =
  'saas_tenant_seo_bundle_history_export_preset_chip_page_persist_enabled';

/** Platform super-admin: max ambiguous export preset label jump candidates before show-more. */
export const PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_JUMP_AMBIGUOUS_LIMIT_KEY =
  'saas_tenant_seo_bundle_history_export_preset_chip_jump_ambiguous_limit';

/** Hard cap for parsed/persisted SEO bundle history length. */
export const TENANT_SEO_BUNDLE_HISTORY_HARD_CAP = 20;
